/* eslint-disable no-nested-ternary */
import 'react-circular-progressbar/dist/styles.css';

import { Icon } from '@components';
import { useScreenSize } from '@hooks';
import { Flex, theme, Typography } from '@styles';
import { ReactComponent as Close } from '@svgs/close.svg';
import { ReactComponent as Download } from '@svgs/download_without_background.svg';
import { ReactComponent as Stop } from '@svgs/stop.svg';
import { bool, func, number } from 'prop-types';
import React, { useMemo } from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import { useTranslation } from 'react-i18next';

import { CenterIcon, StyledFlex } from '../../OfflineContentPage.layoutProps';

const DownloadingStateBadge = ({
  downloadedSizeMB,
  isDownloaded,
  isInProgress,
  onDelete,
  onDownload,
  onStop,
  setIsInProgress,
  sizeMB,
}) => {
  const { i18n } = useTranslation();
  const { isTabletSM } = useScreenSize();

  const percentage = useMemo(
    () => (100 * downloadedSizeMB) / sizeMB,
    [downloadedSizeMB, sizeMB]
  );

  const onDeleteClick = () => {
    onDelete();
    setIsInProgress(false);
  };

  if (Number.isNaN(sizeMB) || sizeMB === 0) {
    return null;
  }

  return (
    <Flex
      flexDirection="column"
      {...(isTabletSM
        ? {
            alignItems: 'center',
            height: 216,
            justifyContent: 'center',
            position: 'absolute',
            width: 'calc(100% - 32px)',
            zIndex: 2,
          }
        : {
            position: 'relative',
            zIndex: 20,
          })}
    >
      <Flex justifyContent="center" p={20}>
        {isInProgress && !isDownloaded ? (
          <Flex
            height={48}
            onClick={onStop}
            pointer
            position="relative"
            width={48}
          >
            <CircularProgressbar
              strokeWidth={16}
              styles={buildStyles({
                pathColor: theme.colors.schoolBusYellow[100],
                trailColor: theme.colors.white[10],
              })}
              value={percentage}
            />
            <CenterIcon SVG={Stop} />
          </Flex>
        ) : (
          <Flex
            alignItems="center"
            border={1}
            pointer
            {...(isDownloaded && { as: StyledFlex })}
            borderColor={
              isDownloaded
                ? theme.colors.schoolBusYellow[100]
                : theme.colors.white[10]
            }
            borderRadius={50}
            borderStyle="solid"
            borderWidth={4}
            height={48}
            onClick={isDownloaded ? onDeleteClick : onDownload}
            width={48}
          >
            <Icon m="auto" SVG={isDownloaded ? Close : Download} />
          </Flex>
        )}
      </Flex>
      <Typography
        color={theme.colors.white[100]}
        opacity="60%"
        textAlign="center"
        variant="MS-regular-s14-lh22"
        whiteSpace="nowrap"
        width="150px"
      >
        {isDownloaded
          ? i18n.t('ProfilePage.Downloaded')
          : downloadedSizeMB
            ? `${downloadedSizeMB}/${sizeMB} ${i18n.t('ProfilePage.MegabytesShort')}`
            : `${sizeMB} ${i18n.t('ProfilePage.MegabytesShort')}`}
      </Typography>
    </Flex>
  );
};

DownloadingStateBadge.propTypes = {
  downloadedSizeMB: number,
  isDownloaded: bool,
  isInProgress: bool,
  onDelete: func.isRequired,
  onDownload: func.isRequired,
  onStop: func.isRequired,
  setIsInProgress: func.isRequired,
  sizeMB: number.isRequired,
};

DownloadingStateBadge.defaultProps = {
  downloadedSizeMB: 0,
  isDownloaded: false,
  isInProgress: false,
};

export default DownloadingStateBadge;
